import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, InputSignal } from '@angular/core';
import { AbcIconCacheService } from './abc-icon-cache.service';

@Component({
  selector: 'abc-icon',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.width]': 'width()',
    '[style.height]': 'height()',
    '[style.display]': '"inline-block"',
    '[style.color]': 'color()',
    '[style.line-height]': '"0"',
  },
})
export class AbcIconComponent {
  private host = inject(ElementRef<HTMLElement>);
  private cacheService = inject(AbcIconCacheService);

  public svgIcon: InputSignal<string> = input('');
  public color: InputSignal<string> = input('currentColor');
  public width: InputSignal<string> = input('25px');
  public height: InputSignal<string> = input('25px');

  constructor() {
    effect(() => {
      this.loadIcon();
    });
  }

  private async loadIcon(): Promise<void> {
    const svgIcon = this.svgIcon();
    const hostElement = this.host.nativeElement;

    if (!svgIcon) {
      hostElement.innerHTML = '';
      return;
    }

    let svgText: string | undefined;

    // Inline SVG
    if (svgIcon.includes('<svg')) {
      svgText = svgIcon;
    }

    // Cached
    else if (this.cacheService.cache().has(svgIcon)) {
      svgText = this.cacheService.cache().get(svgIcon);
    }

    // Fetch
    else {
      try {
        const response = await fetch(svgIcon);
        svgText = await response.text();
        this.cacheService.cache().set(svgIcon, svgText);
      } catch (err) {
        console.error('Failed to fetch SVG:', err);
        hostElement.innerHTML = '';
        return;
      }
    }

    if (!svgText) return;

    hostElement.innerHTML = svgText;

    this.normalizeSvg(hostElement);
  }

  private normalizeSvg(container: HTMLElement): void {
    const svg = container.querySelector('svg');
    if (!svg) return;

    let hasStrokeAttr = false;

    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    // Remove hardcoded fills & strokes
    svg.querySelectorAll('[fill]').forEach((el) => {
      el.removeAttribute('fill');
    });

    svg.querySelectorAll('[stroke]').forEach((el) => {
      el.removeAttribute('stroke');
      hasStrokeAttr = true;
    });

    svg.setAttribute('fill', 'currentColor');

    if (hasStrokeAttr) {
      svg.setAttribute('stroke', 'currentColor');
    }
  }
}
